/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from 'src/features/tyre-management/components/ui/button';
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from 'src/features/tyre-management/components/ui/dialog';
import { Input } from 'src/features/tyre-management/components/ui/input';
import { Label } from 'src/features/tyre-management/components/ui/label';
import { Textarea } from 'src/features/tyre-management/components/ui/textarea';
import { vehicleDetailsApi } from '../api/vehicleDetailsApi';
import useVehicleMutation from '../hooks/useVehicleMutation';

const localDateTime = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const RecordServiceDialog = ({
  open, onOpenChange, vehicleId, service, onSaved
}) => {
  const meterKind = service?.data?.meter_kind || 'distance_km';
  const currentReading = service?.data?.next_due_value && service?.data?.remaining_value
    ? Number(service.data.next_due_value) - Number(service.data.remaining_value)
    : '';
  const initial = useMemo(() => ({
    performed_at: localDateTime(),
    meter_value: currentReading || '',
    service_type: 'Scheduled service',
    provider: '',
    reference: '',
    cost: '',
    currency: 'INR',
    notes: '',
  }), [currentReading]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const recordServiceMutation = useVehicleMutation(
    vehicleId,
    ({ payload, idempotencyKey }) => vehicleDetailsApi.recordService(vehicleId, payload, idempotencyKey)
  );

  useEffect(() => { if (open) setForm(initial); }, [initial, open]);

  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        performed_at: new Date(form.performed_at).toISOString(),
        meter_kind: meterKind,
        policy_version: service.data.policy_version,
        meter_source: 'manual',
        cost: form.cost || null,
      };
      await recordServiceMutation.mutateAsync({
        payload,
        idempotencyKey: `vehicle-service-${vehicleId}-${Date.now()}`,
      });
      onOpenChange(false);
      onSaved();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Record completed service</DialogTitle>
            <DialogDescription>This creates an audited service event and advances the service baseline.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="service-date">Performed at</Label>
                <Input id="service-date" type="datetime-local" value={form.performed_at} onChange={update('performed_at')} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-meter">
                  Meter (
                  {meterKind === 'engine_hours' ? 'hr' : 'km'}
                  )
                </Label>
                <Input id="service-meter" type="number" min="0" step="0.001" value={form.meter_value} onChange={update('meter_value')} required />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="service-type">Service type</Label>
                <Input id="service-type" value={form.service_type} onChange={update('service_type')} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-provider">Provider</Label>
                <Input id="service-provider" value={form.provider} onChange={update('provider')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-reference">Reference</Label>
                <Input id="service-reference" value={form.reference} onChange={update('reference')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-cost">Cost</Label>
                <Input id="service-cost" type="number" min="0" step="0.01" value={form.cost} onChange={update('cost')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-currency">Currency</Label>
                <Input id="service-currency" maxLength="3" value={form.currency} onChange={update('currency')} required />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="service-notes">Notes</Label>
                <Textarea id="service-notes" value={form.notes} onChange={update('notes')} />
              </div>
              {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 sm:col-span-2" role="alert">{error}</div>}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="animate-spin" />}
              {saving ? 'Recording…' : 'Record service'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RecordServiceDialog;
